import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import CardActions from "@/components/base/CardActions";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { useTerritory } from "@/store/territory/TerritoryProvider";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import type { AgenceImmeuble } from "@/pages/agence/data/types";
import { useEmployes } from "@/pages/agence/employes/EmployesProvider";
import { useProprietaires } from "@/pages/agence/proprietaires/ProprietairesProvider";
import { proprietaireNomComplet } from "@/pages/agence/proprietaires/types";
import type { ExportColumn } from "@/utils/export";
import { formatDate } from "@/utils/format";
import ImmeubleForm from "./components/ImmeubleForm";

type SortKey = "recent" | "nom";

const sortOptions = [
  { value: "recent", label: "Plus récents" },
  { value: "nom", label: "Nom (A → Z)" },
];

export default function AgenceImmeublesPage() {
  const { immeubles, biens, contrats, locataires, removeImmeuble, biensByImmeuble, contratsByImmeuble } = useAgenceData();
  const { proprietaires } = useProprietaires();
  const { can } = useEmployes();
  const [searchParams] = useSearchParams();

  const proprietaireParam = searchParams.get("proprietaire") ?? "";
  const openFromQuery = searchParams.get("nouveau") === "1";

  const [formOpen, setFormOpen] = useState(openFromQuery);
  const [search, setSearch] = useState("");
  const [proprietaireFilter, setProprietaireFilter] = useState(proprietaireParam);
  const [regionFilter, setRegionFilter] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");
  const [editing, setEditing] = useState<AgenceImmeuble | null>(null);
  const [toDelete, setToDelete] = useState<AgenceImmeuble | null>(null);

  const hasImmeubles = immeubles.length > 0;
  const noProprietaire = proprietaires.length === 0;

  const proprietaireOptions = useMemo(
    () => [
      { value: "", label: "Tous les propriétaires" },
      ...proprietaires.map((item) => ({ value: item.id, label: proprietaireNomComplet(item) })),
    ],
    [proprietaires]
  );

  const { regionNames } = useTerritory();
  const regionOptions = useMemo(
    () => [
      { value: "", label: "Toutes les régions" },
      ...regionNames.map((name) => ({ value: name, label: name })),
    ],
    [regionNames]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = immeubles.filter((immeuble) => {
      const owner = proprietaires.find((item) => item.id === immeuble.proprietaireId);
      const matchesProprietaire = !proprietaireFilter || immeuble.proprietaireId === proprietaireFilter;
      const matchesRegion = !regionFilter || immeuble.region === regionFilter;
      const matchesSearch =
        !term ||
        [
          immeuble.nom,
          immeuble.commune,
          immeuble.province,
          immeuble.region,
          immeuble.secteur,
          immeuble.adresse,
          owner ? proprietaireNomComplet(owner) : "",
        ]
          .join(" ")
          .toLowerCase()
          .includes(term);
      return matchesProprietaire && matchesRegion && matchesSearch;
    });

    return result.sort((a, b) => {
      if (sort === "nom") return a.nom.localeCompare(b.nom, "fr");
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [immeubles, proprietaires, search, proprietaireFilter, regionFilter, sort]);

  const isFiltering = Boolean(search.trim()) || Boolean(proprietaireFilter) || Boolean(regionFilter);

  const exportColumns: ExportColumn<AgenceImmeuble>[] = [
    { header: "Nom", value: (immeuble) => immeuble.nom },
    {
      header: "Propriétaire",
      value: (immeuble) => {
        const owner = proprietaires.find((item) => item.id === immeuble.proprietaireId);
        return owner ? proprietaireNomComplet(owner) : "";
      },
    },
    { header: "Région", value: (immeuble) => immeuble.region },
    { header: "Province", value: (immeuble) => immeuble.province },
    { header: "Commune", value: (immeuble) => immeuble.commune },
    { header: "Secteur / Quartier", value: (immeuble) => immeuble.secteur },
    { header: "Niveaux", value: (immeuble) => immeuble.niveaux },
    { header: "Adresse", value: (immeuble) => immeuble.adresse },
    { header: "Créé le", value: (immeuble) => formatDate(immeuble.createdAt) },
  ];

  const columns: DataTableColumn<AgenceImmeuble>[] = [
    {
      key: "immeuble",
      header: "Immeuble",
      className: "min-w-[220px]",
      render: (immeuble) => (
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-background-100 text-foreground-300">
            {immeuble.photo ? (
              <img
                src={immeuble.photo}
                alt={immeuble.nom}
                title={`${immeuble.nom} — immeuble à ${immeuble.commune}`}
                className="h-full w-full object-cover object-top"
              />
            ) : (
              <i className="ri-building-2-line text-lg" aria-hidden="true" />
            )}
          </span>
          <span className="truncate font-label font-medium text-foreground-950">{immeuble.nom}</span>
        </div>
      ),
    },
    {
      key: "proprietaire",
      header: "Propriétaire",
      className: "min-w-[180px]",
      render: (immeuble) => {
        const owner = proprietaires.find((item) => item.id === immeuble.proprietaireId);
        return <span className="text-foreground-700">{owner ? proprietaireNomComplet(owner) : "Non défini"}</span>;
      },
    },
    {
      key: "localisation",
      header: "Localisation",
      className: "min-w-[200px]",
      render: (immeuble) => (
        <span className="text-foreground-700">
          {[immeuble.commune, immeuble.province, immeuble.region].filter(Boolean).join(" · ") || "Non renseignée"}
        </span>
      ),
    },
    {
      key: "niveaux",
      header: "Niveaux",
      align: "center",
      render: (immeuble) => (
        <span className="text-foreground-700">{`${immeuble.niveaux} niveau${immeuble.niveaux > 1 ? "x" : ""}`}</span>
      ),
    },
    {
      key: "biens",
      header: "Biens",
      align: "center",
      render: (immeuble) => (
        <span className="font-label font-medium text-foreground-950">{biensByImmeuble(immeuble.id).length}</span>
      ),
    },
    {
      key: "contrats",
      header: "Contrats",
      align: "center",
      render: (immeuble) => (
        <span className="font-label font-medium text-foreground-950">{contratsByImmeuble(immeuble.id).length}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[210px]",
      render: (immeuble) => (
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/agence/immeubles/${immeuble.id}`}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
          >
            Voir le détail
            <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
          </Link>
          <CardActions
            onEdit={can("immeubles.modifier") ? () => setEditing(immeuble) : undefined}
            onDelete={can("immeubles.supprimer") ? () => setToDelete(immeuble) : undefined}
          />
        </div>
      ),
    },
  ];

  const handleAdd = () => setFormOpen(true);

  const resetFilters = () => {
    setSearch("");
    setProprietaireFilter("");
    setRegionFilter("");
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Immeubles</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Gérez les immeubles confiés à l'agence. Chaque immeuble est rattaché à un propriétaire, ce qui permet de
            suivre son patrimoine, ses biens, ses locataires et ses contrats.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <ExportMenu rows={filtered} columns={exportColumns} filename="agence-immeubles" title="Immeubles" />
          {noProprietaire ? (
            <Link
              to="/agence/proprietaires"
              className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-user-star-line text-base" aria-hidden="true" />
              Créer un propriétaire
            </Link>
          ) : (
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Ajouter un immeuble
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-building-2-line" label="Immeubles" value={String(immeubles.length)} tone="primary" />
        <StatCard icon="ri-home-4-line" label="Biens" value={String(biens.length)} tone="secondary" />
        <StatCard icon="ri-user-3-line" label="Locataires" value={String(locataires.length)} tone="accent" />
        <StatCard icon="ri-file-text-line" label="Contrats" value={String(contrats.length)} tone="neutral" />
      </div>

      {hasImmeubles && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-3 lg:max-w-3xl">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="agence-imm-search" className="text-xs font-label font-medium text-foreground-700">
                  Recherche
                </label>
                <Input
                  id="agence-imm-search"
                  icon="ri-search-line"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Nom, commune, secteur, adresse..."
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="agence-imm-proprietaire" className="text-xs font-label font-medium text-foreground-700">
                  Propriétaire
                </label>
                <Select
                  id="agence-imm-proprietaire"
                  value={proprietaireFilter}
                  onChange={(event) => setProprietaireFilter(event.target.value)}
                  options={proprietaireOptions}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="agence-imm-region" className="text-xs font-label font-medium text-foreground-700">
                  Région
                </label>
                <Select
                  id="agence-imm-region"
                  value={regionFilter}
                  onChange={(event) => setRegionFilter(event.target.value)}
                  options={regionOptions}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5 sm:w-52">
              <label htmlFor="agence-imm-sort" className="text-xs font-label font-medium text-foreground-700">
                Trier par
              </label>
              <Select
                id="agence-imm-sort"
                value={sort}
                onChange={(event) => setSort(event.target.value as SortKey)}
                options={sortOptions}
              />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-500">
              {filtered.length} immeuble{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur{" "}
              {immeubles.length}
            </p>
            {isFiltering && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
              >
                <i className="ri-refresh-line text-sm" aria-hidden="true" />
                Réinitialiser
              </button>
            )}
          </div>
        </section>
      )}

      {!hasImmeubles && (
        <EmptyState
          icon="ri-building-2-line"
          title="Aucun immeuble pour le moment"
          description="Créez votre premier immeuble en le rattachant à un propriétaire, avec sa localisation complète. Vous pourrez ensuite y ajouter des biens."
          action={
            noProprietaire ? (
              <Link
                to="/agence/proprietaires"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-user-star-line text-base" aria-hidden="true" />
                Créer d'abord un propriétaire
              </Link>
            ) : (
              <button
                type="button"
                onClick={handleAdd}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-add-line text-base" aria-hidden="true" />
                Ajouter un immeuble
              </button>
            )
          }
        />
      )}

      {hasImmeubles && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun immeuble ne correspond à votre recherche ou aux filtres sélectionnés. Essayez de modifier vos critères."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(immeuble) => immeuble.id}
          minWidthClass="min-w-[1080px]"
          caption="Immeubles"
        />
      )}

      <ImmeubleForm
        open={formOpen || Boolean(editing)}
        initial={editing ?? undefined}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        defaultProprietaireId={proprietaireParam || undefined}
      />

      <ConfirmDeleteModal
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          if (toDelete) removeImmeuble(toDelete.id);
          setToDelete(null);
        }}
        title="Supprimer l'immeuble"
        description={toDelete ? `Voulez-vous vraiment supprimer « ${toDelete.nom} » ? Cette action est définitive.` : ""}
        blockedMessage={
          toDelete && biensByImmeuble(toDelete.id).length > 0
            ? `Cet immeuble contient ${biensByImmeuble(toDelete.id).length} bien(s) rattaché(s). Supprimez ou déplacez d'abord ses biens.`
            : undefined
        }
      />
    </div>
  );
}